import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCRUD } from "../../../api/crud";
import { useBreadcrumbs } from "../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../components/ui/detailView";
import { StatusChip } from "../../../components/ui/statusChip/statusChip.component";
import {
  INVOICES_ENDPOINT,
  INVOICE_PAYMENT_METHOD_LABEL_KEYS,
  INVOICE_STATUS_COLOR_MAP,
  INVOICE_STATUS_LABEL_KEYS,
  KSEF_STATUS_COLOR_MAP,
  KSEF_STATUS_LABEL_KEYS,
  RECEIVER_ROLE_LABEL_KEYS,
  VAT_RATE_LABEL_KEYS,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { Invoice } from "../../../types/invoices";
import { formatDateTimeInline, formatNumber } from "../../../utils";
import { InvoiceParty } from "../invoiceParty/invoiceParty.component";

export default function ViewInvoice() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get } = useCRUD(INVOICES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const canViewInvoices = useHasPermission("invoice.view_invoice");

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    const response = await get(id);
    const fetched = response?.data ?? null;
    setInvoice(fetched);
    setLoading(false);

    // The breadcrumb would otherwise show the raw row id. A draft has no
    // number yet, so it falls back to the buyer - same rule as the heading.
    setOverride(
      `/invoices/${id}`,
      fetched ? fetched.number || fetched.buyer_name : ""
    );
  }, [id, get, setOverride]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // The toolbar issues, sends and files the invoice; it announces the change
  // rather than owning the data, so this view refetches on the same event.
  useEffect(() => {
    window.addEventListener("invoiceUpdated", fetchInvoice);
    return () => window.removeEventListener("invoiceUpdated", fetchInvoice);
  }, [fetchInvoice]);

  if (!canViewInvoices) {
    return null;
  }

  if (loading) {
    return <DetailPage loading />;
  }

  if (!invoice) {
    return <Typography>{t("Invoice not found")}</Typography>;
  }

  return (
    <DetailPage>
      {/* A draft has no number yet, so it falls back to the buyer - the most
          identifying thing it has. Never "Draft": the status chip beside this
          already says that. */}
      <DetailHeader
        icon={<RequestQuoteOutlinedIcon />}
        title={`${t("Invoice")} - ${invoice.number || invoice.buyer_name}`}
        subtitle={invoice.number ? invoice.buyer_name : undefined}
        chips={
          <>
            <StatusChip
              label={t(INVOICE_STATUS_LABEL_KEYS[invoice.status] ?? invoice.status)}
              value={invoice.status}
              colorMap={INVOICE_STATUS_COLOR_MAP}
            />
            <StatusChip
              color={invoice.is_paid ? "success" : "warning"}
              label={invoice.is_paid ? t("Paid") : t("Awaiting payment")}
            />
            <StatusChip
              label={`KSeF: ${t(KSEF_STATUS_LABEL_KEYS[invoice.ksef_status] ?? invoice.ksef_status)}`}
              value={invoice.ksef_status}
              colorMap={KSEF_STATUS_COLOR_MAP}
            />
            {invoice.is_overdue && <StatusChip color="error" label={t("Overdue")} />}
          </>
        }
      >
        {invoice.ksef_error && (
          <Alert severity="error">
            {t("KSeF rejected this invoice")}: {invoice.ksef_error}
          </Alert>
        )}
        {invoice.ksef_number && (
          <Alert severity="success">
            {t("KSeF number")}: {invoice.ksef_number}
          </Alert>
        )}
      </DetailHeader>

      <DetailGrid>
        <InvoiceParty
          title={t("Seller")}
          name={invoice.seller_name}
          street={invoice.seller_street}
          zipCode={invoice.seller_zip_code}
          city={invoice.seller_city}
          identifier={invoice.seller_nip}
        />
        <InvoiceParty
          title={t("Buyer")}
          name={invoice.buyer_name}
          street={invoice.buyer_street}
          zipCode={invoice.buyer_zip_code}
          city={invoice.buyer_city}
          identifier={invoice.buyer_nip}
        />
        {invoice.receiver_role && (
          <InvoiceParty
            title={t("Receiver")}
            name={invoice.receiver_name}
            street={invoice.receiver_street}
            zipCode={invoice.receiver_zip_code}
            city={invoice.receiver_city}
            identifier={invoice.receiver_nip}
          >
            <DetailRow
              label={t("Role")}
              value={t(RECEIVER_ROLE_LABEL_KEYS[invoice.receiver_role] ?? invoice.receiver_role)}
            />
          </InvoiceParty>
        )}

        <DetailSection title={t("Dates and payment")}>
          <DetailRow label={t("Date of issue")} value={invoice.issued_at} />
          <DetailRow label={t("Date of sale")} value={invoice.sold_at} />
          <DetailRow label={t("Payment due")} value={invoice.due_date} />
          <DetailRow
            label={t("Payment method")}
            value={t(INVOICE_PAYMENT_METHOD_LABEL_KEYS[invoice.payment_method] ?? invoice.payment_method)}
          />
          {invoice.paid_at && <DetailRow label={t("Paid at")} value={invoice.paid_at} />}
        </DetailSection>
      </DetailGrid>

      <DetailSection title={t("Invoice items")}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("No.")}</TableCell>
                <TableCell>{t("Name")}</TableCell>
                <TableCell align="right">{t("Qty")}</TableCell>
                <TableCell>{t("Unit of measure")}</TableCell>
                <TableCell align="right">{t("Net price")}</TableCell>
                <TableCell align="right">{t("Net value")}</TableCell>
                <TableCell align="right">{t("VAT")}</TableCell>
                <TableCell align="right">{t("VAT amount")}</TableCell>
                <TableCell align="right">{t("Gross value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items_detail.map((item) => (
                <TableRow key={item.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell>{item.ordinal}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{formatNumber(item.quantity)}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">{formatNumber(item.price_net)}</TableCell>
                  <TableCell align="right">{formatNumber(item.total_net)}</TableCell>
                  <TableCell align="right">
                    {t(VAT_RATE_LABEL_KEYS[item.vat_rate] ?? item.vat_rate)}
                  </TableCell>
                  <TableCell align="right">{formatNumber(item.total_vat)}</TableCell>
                  <TableCell align="right">{formatNumber(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DetailSection>

      <DetailGrid>
        <DetailSection title={t("VAT summary")}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("VAT rate")}</TableCell>
                  <TableCell align="right">{t("Net")}</TableCell>
                  <TableCell align="right">{t("VAT")}</TableCell>
                  <TableCell align="right">{t("Gross")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.vat_summary.map((row) => (
                  <TableRow key={row.vat_rate} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell>{t(VAT_RATE_LABEL_KEYS[row.vat_rate] ?? row.vat_rate)}</TableCell>
                    <TableCell align="right">{formatNumber(row.net)}</TableCell>
                    <TableCell align="right">{formatNumber(row.vat)}</TableCell>
                    <TableCell align="right">{formatNumber(row.gross)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DetailSection>

        <DetailSection title={t("Summary")}>
          <DetailRow label={t("Net total")} value={`${formatNumber(invoice.total_net)} ${invoice.currency}`} />
          <DetailRow label={t("VAT total")} value={`${formatNumber(invoice.total_vat)} ${invoice.currency}`} />
          <DetailRow label={t("To pay")}>
            <DetailValue>
              <strong>{`${formatNumber(invoice.total)} ${invoice.currency}`}</strong>
            </DetailValue>
          </DetailRow>
        </DetailSection>
      </DetailGrid>

      {invoice.notes && (
        <DetailSection title={t("Notes")} padded>
          <DetailValue multiline>{invoice.notes}</DetailValue>
        </DetailSection>
      )}

      <DetailMetadata
        createdAt={invoice.created_at}
        updatedAt={invoice.updated_at}
        items={[
          {
            icon: <SendOutlinedIcon />,
            label: t("Sent to client"),
            value: invoice.sent_to_client_at
              ? [formatDateTimeInline(invoice.sent_to_client_at), invoice.sent_to_email && `(${invoice.sent_to_email})`]
                  .filter(Boolean)
                  .join(" ")
              : null,
          },
        ]}
      />
    </DetailPage>
  );
}
